import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ActionIcon,
  Input as MantineInput,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { useRealStudents } from "@/hooks/useRealStudents";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";
import {
  Users,
  Plus,
  Trash2,
  Upload,
  Download,
  UserPlus,
  AlertTriangle,
  Settings,
  ChevronDown,
  ChevronRight,
  Edit,
  ListChecks,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/auth";
import { UnifiedLoadingSpinner } from "@/components/loading/UnifiedLoadingSpinner";
import { Modal, Stack, Button as MantineBtn, Checkbox } from "@mantine/core";
import { supabase } from "@/integrations/supabase/client";
import { IconEdit, IconTrash, IconUserCheck } from "@tabler/icons-react";
import { getVisiblePaginationPages } from "@/utils/getVisiblePaginationPages";

interface StudentForm {
  name: string;
  grade: string;
  section: string;
  studentId: string;
}

const Students = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [selectedStage, setSelectedStage] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [groupByStage, setGroupByStage] = useState(false);
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [editingStudent, setEditingStudent] = useState<any>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set()
  );
  const [massDeleteOpen, setMassDeleteOpen] = useState(false);
  const [deleteAllSchoolOpen, setDeleteAllSchoolOpen] = useState(false);
  const [deleteAllTotalCount, setDeleteAllTotalCount] = useState<number | null>(
    null
  );
  const [deleteAllCountLoading, setDeleteAllCountLoading] = useState(false);

  const itemsPerPage = 18;

  const {
    schoolId,
    students,
    addStudent,
    deleteStudent: removeStudent,
    deleteStudents,
    deleteAllStudents,
    deletingStudents,
    updateStudent,
    loading: studentsLoading,
  } = useRealStudents();
  const { settings, loading, forceRefresh } = useRealSchoolSettings();
  const { user } = useAuth();
  const { toast } = useToast();
  const form = useForm<StudentForm>();
  const editForm = useForm<StudentForm>();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [studentToInvite, setStudentToInvite] = useState(null);

  const inviteUser = async () => {
    try {
      if (studentToInvite?.email) {
        setStudentToInvite({ ...studentToInvite, loading: true });
        const { data, error } = await supabase.functions.invoke("create-user", {
          body: {
            email: studentToInvite.email,
            full_name: studentToInvite.name,
            role: "student",
            student_id: studentToInvite.id,
            school_id: studentToInvite?.school_id,
            withoutInviteEmail: false,
          },
        });
        setStudentToInvite({ ...studentToInvite, loading: true });
        setStudentToInvite(null);
        if (error) {
          const errorMessage = await error.context.json();

          console.error(errorMessage);

          throw errorMessage;
        }

        if (data?.success) {
          toast({
            title: "تم إرسال الدعوة بنجاح",
            description: `تم إرسال دعوة إلى ${studentToInvite.email}`,
          });
          setIsInviteModalOpen(false);
        }
      }
    } catch (error) {
      console.error("Error during logout:", error);
      toast({
        title: "خطأ أثناء إرسال الدعوة",
        description: error?.message ?? "حدث خطأ أثناء إرسال دعوة الطالب",
        variant: "destructive",
      });
    }
  };

  const getAvailableGrades = () => {
    if (!settings) return [];
    const grades: string[] = [];
    settings.stages.forEach((stage) => {
      if (stage.enabled) {
        stage.grades.forEach((grade: any) => {
          grades.push(grade.name);
        });
      }
    });
    return grades;
  };

  const getAvailableSections = (gradeName: string) => {
    if (!settings) return [];
    for (const stage of settings.stages) {
      if (stage.enabled) {
        const grade = stage.grades.find((g: any) => g.name === gradeName);
        if (grade) {
          return grade.sections || ["أ"];
        }
      }
    }
    return ["أ"];
  };

  const availableGrades = getAvailableGrades();
  const selectedGradeForForm = form.watch("grade");
  const availableSections = selectedGradeForForm
    ? getAvailableSections(selectedGradeForForm)
    : [];

  // Helper functions for stage filtering
  const enabledStages = settings
    ? settings.stages.filter((stage: any) => stage.enabled)
    : [];
  const hasMultipleStages = enabledStages.length > 1;
  const isConfigured =
    settings && settings.school_name && settings.school_name.trim().length > 0;

  const getGradesByStage = (stageName: string) => {
    if (!settings) return [];
    const stage = settings.stages.find(
      (s: any) => s.name === stageName && s.enabled
    );
    return stage ? stage.grades.map((g: any) => g.name) : [];
  };

  const getStageByGrade = (gradeName: string) => {
    if (!settings) return null;
    for (const stage of settings.stages) {
      if (
        stage.enabled &&
        stage.grades.some((g: any) => g.name === gradeName)
      ) {
        return stage.name;
      }
    }
    return null;
  };

  // Get available grades based on selected stage
  const getFilteredGrades = () => {
    if (selectedStage) {
      return getGradesByStage(selectedStage);
    }
    return availableGrades;
  };

  const filteredGrades = getFilteredGrades();

  // فلترة الطلاب
  const filteredStudents = students.filter((student) => {
    const stageMatch =
      !selectedStage || getStageByGrade(student.grade) === selectedStage;
    const gradeMatch = !selectedGrade || student.grade === selectedGrade;
    const sectionMatch =
      !selectedSection || student.section === selectedSection;
    return stageMatch && gradeMatch && sectionMatch;
  });

  // Calculate pagination for filtered students
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStudents = filteredStudents.slice(startIndex, endIndex);

  // Group students by stage
  const groupedStudents = () => {
    if (!groupByStage || !hasMultipleStages)
      return { ungrouped: filteredStudents };

    const grouped: { [key: string]: any[] } = {};

    filteredStudents.forEach((student) => {
      const stageName = getStageByGrade(student.grade) || "غير محدد";
      if (!grouped[stageName]) {
        grouped[stageName] = [];
      }
      grouped[stageName].push(student);
    });

    return grouped;
  };

  const toggleStageExpansion = (stageName: string) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageName)) {
      newExpanded.delete(stageName);
    } else {
      newExpanded.add(stageName);
    }
    setExpandedStages(newExpanded);
  };

  const onSubmit = async (data: StudentForm) => {
    // التحقق من عدم تكرار رقم الطالب
    const existingStudent = students.find(
      (s) => s.student_id === data.studentId
    );

    if (existingStudent) {
      toast({
        title: "خطأ",
        description: "رقم الطالب موجود مسبقاً",
        variant: "destructive",
      });
      return;
    }

    const result = await addStudent({
      name: data.name,
      grade: data.grade,
      section: data.section,
      student_id: data.studentId,
      status: "active",
    });

    if (result) {
      form.reset();
      setIsDialogOpen(false);

      toast({
        title: "تم إضافة الطالب بنجاح",
        description: `تم إضافة ${data.name} إلى ${data.grade} شعبة ${data.section}`,
      });
    }
  };

  const handleBulkAdd = async () => {
    if (!selectedGrade || !selectedSection) {
      toast({
        title: "خطأ",
        description: "يرجى اختيار الصف والشعبة أولاً",
        variant: "destructive",
      });
      return;
    }

    const lines = bulkText.trim().split("\n");
    const newStudents = lines
      .filter((line) => line.trim())
      .map((line, index) => {
        const parts = line.trim().split("\t");
        const name = parts[0] || line.trim();
        const studentId = parts[1] || `STU${Date.now()}${index}`;

        return {
          name,
          grade: selectedGrade,
          section: selectedSection,
          studentId,
        };
      })
      .filter(
        (student) =>
          student.name &&
          !students.find((s) => s.student_id === student.studentId)
      );

    if (newStudents.length > 0) {
      // Add students one by one since useRealStudents doesn't have addMultipleStudents
      for (const student of newStudents) {
        await addStudent({
          name: student.name,
          grade: student.grade,
          section: student.section,
          student_id: student.studentId,
          status: "active",
        });
      }

      setBulkText("");
      setSelectedStage("");
      setSelectedGrade("");
      setSelectedSection("");

      toast({
        title: "تم إضافة الطلاب بنجاح",
        description: `تم إضافة ${newStudents.length} طالب`,
      });
    }
  };

  const handleRemoveStudent = async (id: string, _name: string) => {
    try {
      await removeStudent(id);
    } catch {
      // useRealStudents delete mutation already shows onError toast
    }
  };

  const toggleBulkSelectMode = () => {
    setIsBulkSelectMode((on) => {
      if (on) setSelectedIds(new Set());
      return !on;
    });
  };

  const toggleStudentSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllInList = (list: typeof students) => {
    setSelectedIds((prev) => {
      const allSelected =
        list.length > 0 && list.every((s) => prev.has(s.id));
      const next = new Set(prev);
      if (allSelected) list.forEach((s) => next.delete(s.id));
      else list.forEach((s) => next.add(s.id));
      return next;
    });
  };

  const handleMassDelete = async () => {
    try {
      await deleteStudents(Array.from(selectedIds));
      setSelectedIds(new Set());
      setMassDeleteOpen(false);
    } catch {
      // deleteStudents mutation onError toast
    }
  };

  useEffect(() => {
    if (!deleteAllSchoolOpen || !schoolId) return;
    let cancelled = false;
    setDeleteAllTotalCount(null);
    setDeleteAllCountLoading(true);
    void (async () => {
      const { count, error } = await supabase
        .from("students")
        .select("*", { count: "exact", head: true })
        .eq("school_id", schoolId);
      if (cancelled) return;
      setDeleteAllCountLoading(false);
      if (error) {
        setDeleteAllTotalCount(null);
        return;
      }
      setDeleteAllTotalCount(count ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [deleteAllSchoolOpen, schoolId]);

  const handleDeleteAllSchoolStudents = async () => {
    try {
      await deleteAllStudents();
      setSelectedIds(new Set());
      setIsBulkSelectMode(false);
      setDeleteAllSchoolOpen(false);
    } catch {
      // deleteAllStudents mutation onError toast
    }
  };

  // Reset filters when stage changes
  const handleStageChange = (stage: string) => {
    setSelectedStage(stage);
    setSelectedGrade("");
    setSelectedSection("");
    setCurrentPage(1);
  };

  // Reset section when grade changes
  const handleGradeChange = (grade: string) => {
    setSelectedGrade(grade);
    setSelectedSection("");
    setCurrentPage(1);
  };

  const handleEditStudent = (student: any) => {
    setEditingStudent(student);
    editForm.reset({
      name: student.name,
      grade: student.grade,
      section: student.section,
      studentId: student.studentId,
    });
    setIsEditDialogOpen(true);
  };

  const onEditSubmit = (data: StudentForm) => {
    if (!editingStudent) return;

    // التحقق من عدم تكرار رقم الطالب (إذا تم تغييره)
    if (data.studentId !== editingStudent.studentId) {
      const existingStudent = students.find(
        (s) => s.student_id === data.studentId && s.id !== editingStudent.id
      );
      if (existingStudent) {
        toast({
          title: "خطأ",
          description: "رقم الطالب موجود مسبقاً",
          variant: "destructive",
        });
        return;
      }
    }

    updateStudent(editingStudent.id, data);
    setIsEditDialogOpen(false);
    setEditingStudent(null);

    toast({
      title: "تم تحديث الطالب بنجاح",
      description: `تم تحديث بيانات ${data.name}`,
    });
  };

  if (loading || studentsLoading) {
    return (
      <div
        className="container mx-auto px-4 py-2 flex justify-center items-center min-h-screen"
        dir="rtl"
      >
        <UnifiedLoadingSpinner size="md" />
      </div>
    );
  }

  if (!user) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100"
        dir="rtl"
      >
        <div className="container mx-auto px-4 py-8">
          <Alert className="max-w-2xl mx-auto border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-center">
              يجب تسجيل الدخول أولاً للوصول إلى بيانات الطلاب.
              <Link to="/auth" className="font-bold underline mr-2">
                تسجيل الدخول
              </Link>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100"
        dir="rtl"
      >
        <div className="container mx-auto px-4 py-8">
          <Alert className="max-w-2xl mx-auto border-orange-200 bg-orange-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-center">
              يجب إعداد المدرسة أولاً قبل إضافة الطلاب.
              <Link to="/school-settings" className="font-bold underline mr-2">
                اذهب إلى إعدادات المدرسة
              </Link>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const studentGroups = groupedStudents();

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100"
      dir="rtl"
    >
      <div className="container mx-auto px-4 py-8">
        {/* Main Students Management Card */}
        <Card className="bg-white/70 backdrop-blur-sm border-white/20 mb-6">
          <CardHeader className="bg-secondary text-white rounded-t-lg">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="flex items-center gap-2 text-white text-lg">
                <Users className="h-5 w-5 text-white" />
                إدارة الطلاب
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Bulk Add Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Upload className="h-5 w-5" />
                إضافة طلاب متعددين
              </h3>
              <p className="text-gray-600 mb-4 text-sm">
                أدخل أسماء الطلاب (كل اسم في سطر منفصل) أو انسخ من ملف Excel
              </p>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {hasMultipleStages && (
                    <div className="w-full">
                      <Label htmlFor="bulkStage" className="text-sm">
                        المرحلة
                      </Label>
                      <Select
                        value={selectedStage}
                        onValueChange={setSelectedStage}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="اختر المرحلة" />
                        </SelectTrigger>
                        <SelectContent>
                          {enabledStages.map((stage) => (
                            <SelectItem key={stage.id} value={stage.name}>
                              {stage.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="w-full">
                    <Label htmlFor="bulkGrade" className="text-sm">
                      الصف
                    </Label>
                    <Select
                      value={selectedGrade}
                      onValueChange={setSelectedGrade}
                      disabled={hasMultipleStages && !selectedStage}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            hasMultipleStages && !selectedStage
                              ? "اختر المرحلة أولاً"
                              : "اختر الصف"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredGrades.map((grade) => (
                          <SelectItem key={grade} value={grade}>
                            {grade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-full">
                    <Label htmlFor="bulkSection" className="text-sm">
                      الشعبة
                    </Label>
                    <Select
                      value={selectedSection}
                      onValueChange={setSelectedSection}
                      disabled={!selectedGrade}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            !selectedGrade ? "اختر الصف أولاً" : "اختر الشعبة"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedGrade &&
                          getAvailableSections(selectedGrade).map((section) => (
                            <SelectItem key={section} value={section}>
                              شعبة {section}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="w-full">
                  <Label htmlFor="bulkText" className="text-sm">
                    أسماء الطلاب
                  </Label>
                  <textarea
                    id="bulkText"
                    className="w-full h-32 p-3 border border-gray-300 rounded-md resize-none"
                    placeholder="أدخل أسماء الطلاب هنا...&#10;كل اسم في سطر منفصل&#10;أحمد محمد&#10;فاطمة علي&#10;خالد عبدالله"
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                  />
                </div>
                <Button
                  onClick={handleBulkAdd}
                  disabled={
                    !bulkText.trim() || !selectedGrade || !selectedSection
                  }
                  className="w-full sm:w-auto"
                >
                  إضافة جميع الطلاب
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Students Table with Filters */}
        <Card className="bg-white/70 backdrop-blur-sm border-white/20">
          <CardHeader className="bg-secondary text-white rounded-t-lg">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-white text-lg">
                  <Users className="h-5 w-5 text-white" />
                  قائمة الطلاب ({filteredStudents.length})
                </CardTitle>
                <CardDescription className="text-sm text-white/90">
                  إدارة بيانات الطلاب المسجلين
                </CardDescription>
              </div>
              <Dialog
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                key={"add-student-dialog"}
              >
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2 w-full sm:w-auto bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white">
                    <Plus className="h-4 w-4" />
                    إضافة طالب جديد
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>إضافة طالب جديد</DialogTitle>
                    <DialogDescription>
                      أدخل بيانات الطالب الجديد
                    </DialogDescription>
                  </DialogHeader>
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmit)}
                      className="space-y-4"
                    >
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>اسم الطالب</FormLabel>
                            <FormControl>
                              <Input placeholder="أحمد محمد علي" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="grade"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>الصف</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="اختر الصف" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableGrades.map((grade) => (
                                  <SelectItem key={grade} value={grade}>
                                    {grade}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="section"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>الشعبة</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="اختر الشعبة" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableSections.map((section) => (
                                  <SelectItem key={section} value={section}>
                                    شعبة {section}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* <FormField
                        control={form.control}
                        name="studentId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>رقم الطالب</FormLabel>
                            <FormControl>
                              <Input placeholder="STU001" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      /> */}

                      <Button type="submit" className="w-full">
                        إضافة الطالب
                      </Button>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Filters + actions: one row on large screens; buttons never wrap to a second column */}
            <div className="mb-4 flex flex-col gap-4 rounded-lg border border-white/30 bg-white/10 p-4 shadow-sm xl:flex-row xl:flex-nowrap xl:items-end xl:gap-3">
              <div className="grid min-w-0 flex-1 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {hasMultipleStages && (
                  <div className="w-full min-w-0 lg:col-span-1">
                    <Label className="text-sm text-white">
                      فلترة حسب المرحلة
                    </Label>
                    <Select
                      value={selectedStage}
                      onValueChange={handleStageChange}
                    >
                      <SelectTrigger className="h-8 bg-white/20 border-white/30 text-white placeholder:text-white/70">
                        <SelectValue placeholder="جميع المراحل" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">جميع المراحل</SelectItem>
                        {enabledStages.map((stage) => (
                          <SelectItem key={stage.id} value={stage.name}>
                            {stage.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="w-full min-w-0">
                  <Label className="text-sm text-white">فلترة حسب الصف</Label>
                  <Select
                    value={selectedGrade}
                    onValueChange={handleGradeChange}
                    disabled={hasMultipleStages && !selectedStage}
                  >
                    <SelectTrigger className="h-8 bg-white/20 border-white/30 text-white placeholder:text-white/70">
                      <SelectValue placeholder="جميع الصفوف" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">جميع الصفوف</SelectItem>
                      {filteredGrades.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full min-w-0">
                  <Label className="text-sm text-white">فلترة حسب الشعبة</Label>
                  <Select
                    value={selectedSection}
                    onValueChange={setSelectedSection}
                    disabled={!selectedGrade}
                  >
                    <SelectTrigger className="h-8 bg-white/20 border-white/30 text-white placeholder:text-white/70">
                      <SelectValue placeholder="جميع الشعب" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">جميع الشعب</SelectItem>
                      {selectedGrade &&
                        getAvailableSections(selectedGrade).map((section) => (
                          <SelectItem key={section} value={section}>
                            شعبة {section}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex shrink-0 flex-row flex-nowrap items-end justify-start gap-2 overflow-x-auto pb-0.5 xl:justify-end">
                <Tooltip label="تحديد عدة طلاب ثم حذفهم دفعة واحدة (حذف نهائي من قاعدة البيانات)">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={
                      !isBulkSelectMode
                        ? "shrink-0 scale-[1.02] border-2 border-cyan-300 bg-cyan-500/35 font-semibold text-white shadow-md ring-2 ring-cyan-300/55 hover:bg-cyan-400/45 hover:ring-cyan-200/70"
                        : selectedIds.size > 0
                          ? "shrink-0 scale-[1.02] border-2 border-violet-500 bg-violet-100 font-bold text-violet-950 shadow-lg ring-2 ring-violet-400/70 hover:bg-violet-50"
                          : "shrink-0 scale-[1.02] border-2 border-emerald-400 bg-emerald-50 font-bold text-emerald-950 shadow-lg ring-2 ring-emerald-300/75 hover:bg-emerald-100"
                    }
                    onClick={toggleBulkSelectMode}
                    aria-pressed={isBulkSelectMode}
                    aria-label="وضع تحديد عدة طلاب"
                  >
                    <ListChecks className="h-4 w-4 ml-1 inline shrink-0" />
                    تحديد متعدد
                  </Button>
                </Tooltip>
                <Tooltip label="حذف نهائي لكل سجلات الطلاب لهذه المدرسة من قاعدة البيانات (جميع الحالات)">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 border-2 border-red-300/90 bg-red-600/25 font-semibold text-white shadow-md ring-1 ring-red-300/50 hover:bg-red-600/40 hover:text-white"
                    onClick={() => setDeleteAllSchoolOpen(true)}
                    disabled={deletingStudents}
                    aria-label="حذف كل طلاب المدرسة"
                  >
                    <Trash2 className="h-4 w-4 ml-1 inline shrink-0" />
                    حذف كل طلاب المدرسة
                  </Button>
                </Tooltip>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
                  onClick={() => {
                    setSelectedStage("");
                    setSelectedGrade("");
                    setSelectedSection("");
                    setGroupByStage(false);
                    setCurrentPage(1);
                  }}
                >
                  إلغاء الفلترة
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {isBulkSelectMode && selectedIds.size > 0 && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/90 p-3 text-emerald-950 shadow-sm">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <span className="flex h-8 min-w-8 items-center justify-center rounded-full bg-emerald-700 px-2 text-xs font-bold text-white shadow-sm">
                    {selectedIds.size}
                  </span>
                  {selectedIds.size === 1 ? "طالب محدد" : "طلاب محددون"} — جاهز
                  للحذف النهائي
                </span>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="shrink-0 font-semibold"
                  onClick={() => setMassDeleteOpen(true)}
                  disabled={deletingStudents}
                >
                  حذف المحددين نهائياً
                </Button>
              </div>
            )}
            {filteredStudents.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                لا توجد طلاب مطابقون للفلترة المحددة
              </div>
            ) : groupByStage && hasMultipleStages ? (
              // Grouped view by stage
              <div className="space-y-6">
                {Object.entries(studentGroups).map(
                  ([stageName, stageStudents]) => (
                    <div
                      key={stageName}
                      className="border rounded-lg overflow-hidden"
                    >
                      <div
                        className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleStageExpansion(stageName)}
                      >
                        <div className="flex items-center gap-2">
                          {expandedStages.has(stageName) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <h3 className="font-semibold text-lg">{stageName}</h3>
                          <Badge variant="secondary" className="mr-2">
                            {stageStudents.length} طالب
                          </Badge>
                        </div>
                      </div>

                      {expandedStages.has(stageName) && (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {isBulkSelectMode && (
                                  <TableHead className="w-9 min-w-[2.25rem] border-b-2 border-emerald-600 bg-emerald-50 py-2 align-middle">
                                    <div className="flex justify-center px-0.5">
                                      <div className="rounded-md border border-emerald-600/80 bg-white p-[3px] shadow-sm">
                                        <Checkbox
                                          size="xs"
                                          variant="outline"
                                          color="teal"
                                          checked={
                                            stageStudents.length > 0 &&
                                            stageStudents.every((s) =>
                                              selectedIds.has(s.id)
                                            )
                                          }
                                          indeterminate={
                                            stageStudents.some((s) =>
                                              selectedIds.has(s.id)
                                            ) &&
                                            !stageStudents.every((s) =>
                                              selectedIds.has(s.id)
                                            )
                                          }
                                          onChange={() =>
                                            toggleSelectAllInList(stageStudents)
                                          }
                                          aria-label={`تحديد كل طلاب ${stageName}`}
                                        />
                                      </div>
                                    </div>
                                  </TableHead>
                                )}
                                <TableHead className="w-12">م</TableHead>
                                <TableHead className="min-w-[120px]">
                                  اسم الطالب
                                </TableHead>
                                <TableHead className="min-w-[80px]">
                                  الصف
                                </TableHead>
                                <TableHead className="min-w-[80px]">
                                  الشعبة
                                </TableHead>
                                <TableHead className="min-w-[100px]">
                                  الإجراءات
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {stageStudents.map((student, index) => (
                                <TableRow
                                  key={student.id}
                                  className={
                                    isBulkSelectMode &&
                                    selectedIds.has(student.id)
                                      ? "bg-slate-50"
                                      : undefined
                                  }
                                >
                                  {isBulkSelectMode && (
                                    <TableCell
                                      className={`w-8 min-w-[2rem] border-l border-slate-200 px-0.5 py-1 align-middle ${
                                        selectedIds.has(student.id)
                                          ? "bg-slate-100"
                                          : ""
                                      }`}
                                    >
                                      <div className="flex justify-center">
                                        <Checkbox
                                          size="xs"
                                          variant="outline"
                                          color="gray"
                                          checked={selectedIds.has(student.id)}
                                          onChange={() =>
                                            toggleStudentSelection(student.id)
                                          }
                                          aria-label={`تحديد ${student.name}`}
                                        />
                                      </div>
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    <Badge variant="outline">{index + 1}</Badge>
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {student.name}
                                  </TableCell>
                                  <TableCell>{student.grade}</TableCell>
                                  <TableCell>
                                    <Badge variant="secondary">
                                      شعبة {student.section}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="flex gap-1 flex-col sm:flex-row">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditStudent(student)}
                                      className="text-blue-600 hover:text-blue-800"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-600 hover:text-red-800"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent
                                        dir="ltr"
                                        className="text-left"
                                      >
                                        <AlertDialogHeader>
                                          <AlertDialogTitle className="text-right">
                                            تأكيد حذف الطالب
                                          </AlertDialogTitle>
                                          <AlertDialogDescription className="text-right">
                                            هل أنت متأكد من حذف الطالب &quot;
                                            {student.name}&quot; من الصف{" "}
                                            {student.grade} شعبة{" "}
                                            {student.section}؟ سيتم الحذف نهائياً
                                            من قاعدة البيانات بما في ذلك سجلات
                                            حضوره وتأخره.
                                            <br />
                                            <span className="text-red-600 font-semibold">
                                              لا يمكن التراجع عن هذا الإجراء.
                                            </span>
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>
                                            إلغاء
                                          </AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() =>
                                              handleRemoveStudent(
                                                student.id,
                                                student.name
                                              )
                                            }
                                            className="bg-red-600 hover:bg-red-700 text-white"
                                          >
                                            حذف الطالب
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            ) : (
              // Regular table view
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {isBulkSelectMode && (
                        <TableHead className="w-9 min-w-[2.25rem] border-b-2 border-emerald-600 bg-emerald-50 py-2 align-middle">
                          <div className="flex justify-center px-0.5">
                            <div className="rounded-md border border-emerald-600/80 bg-white p-[3px] shadow-sm">
                              <Checkbox
                                size="xs"
                                variant="outline"
                                color="teal"
                                checked={
                                  currentStudents.length > 0 &&
                                  currentStudents.every((s) =>
                                    selectedIds.has(s.id)
                                  )
                                }
                                indeterminate={
                                  currentStudents.some((s) =>
                                    selectedIds.has(s.id)
                                  ) &&
                                  !currentStudents.every((s) =>
                                    selectedIds.has(s.id)
                                  )
                                }
                                onChange={() =>
                                  toggleSelectAllInList(currentStudents)
                                }
                                aria-label="تحديد كل الطلاب في هذه الصفحة"
                              />
                            </div>
                          </div>
                        </TableHead>
                      )}
                      <TableHead className="w-12">م</TableHead>
                      <TableHead className="min-w-[120px]">
                        اسم الطالب
                      </TableHead>
                      {hasMultipleStages && (
                        <TableHead className="min-w-[80px]">المرحلة</TableHead>
                      )}
                      <TableHead className="min-w-[80px]">الصف</TableHead>
                      <TableHead className="min-w-[80px]">الشعبة</TableHead>
                      <TableHead className="min-w-[100px]">الإجراءات</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentStudents.map((student, index) => (
                      <TableRow
                        key={student.id}
                        className={
                          isBulkSelectMode && selectedIds.has(student.id)
                            ? "bg-slate-50"
                            : undefined
                        }
                      >
                        {isBulkSelectMode && (
                          <TableCell
                            className={`w-8 min-w-[2rem] border-l border-slate-200 px-0.5 py-1 align-middle ${
                              selectedIds.has(student.id)
                                ? "bg-slate-100"
                                : ""
                            }`}
                          >
                            <div className="flex justify-center">
                              <Checkbox
                                size="xs"
                                variant="outline"
                                color="gray"
                                checked={selectedIds.has(student.id)}
                                onChange={() =>
                                  toggleStudentSelection(student.id)
                                }
                                aria-label={`تحديد ${student.name}`}
                              />
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge variant="outline">
                            {startIndex + index + 1}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {student.name}
                        </TableCell>
                        {hasMultipleStages && (
                          <TableCell>
                            <Badge variant="secondary">
                              {getStageByGrade(student.grade)}
                            </Badge>
                          </TableCell>
                        )}
                        <TableCell>{student.grade}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            شعبة {student.section}
                          </Badge>
                        </TableCell>
                        <TableCell className="flex gap-1 flex-col sm:flex-row">
                          {" "}
                          {/* <Tooltip label="تعديل الطالب"> */}
                          <ActionIcon
                            variant="light"
                            size="lg"
                            onClick={() => handleEditStudent(student)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <IconEdit className="h-4 w-4" />
                          </ActionIcon>{" "}
                          {/* </Tooltip> */}
                          {/* <Tooltip label="استدعاء الطالب بالبريد الإلكتروني"> */}
                          <ActionIcon
                            variant="subtle"
                            size="lg"
                            color="primary"
                            aria-label="Settings"
                            onClick={() => {
                              setIsInviteModalOpen(true);
                              setStudentToInvite(student);
                            }}
                          >
                            <IconUserCheck
                              style={{ width: "70%", height: "70%" }}
                              stroke={1.5}
                            />
                          </ActionIcon>
                          {/* </Tooltip> */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              {/* <Tooltip label="حذف الطالب"> */}
                              <ActionIcon
                                variant="light"
                                color="red"
                                size="lg"
                                className="text-red-600 hover:text-red-800"
                              >
                                <IconTrash className="h-4 w-4" />
                              </ActionIcon>
                              {/* </Tooltip> */}
                            </AlertDialogTrigger>
                            <AlertDialogContent dir="ltr" className="text-left">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-right">
                                  تأكيد حذف الطالب
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-right">
                                  هل أنت متأكد من حذف الطالب &quot;
                                  {student.name}&quot; من الصف {student.grade}{" "}
                                  شعبة {student.section}؟ سيتم الحذف نهائياً من
                                  قاعدة البيانات بما في ذلك سجلات حضوره
                                  وتأخره.
                                  <br />
                                  <span className="text-red-600 font-semibold">
                                    لا يمكن التراجع عن هذا الإجراء.
                                  </span>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>إلغاء</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    handleRemoveStudent(
                                      student.id,
                                      student.name
                                    )
                                  }
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                  حذف الطالب
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {!groupByStage && totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-t border-slate-200 mt-4 rounded-b-lg">
                <div className="text-sm text-slate-600">
                  عرض {startIndex + 1} إلى{" "}
                  {Math.min(endIndex, filteredStudents.length)} من{" "}
                  {filteredStudents.length} طالب
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm"
                  >
                    السابق
                  </Button>
                  <div className="flex gap-1">
                    {getVisiblePaginationPages(currentPage, totalPages, 5).map(
                      (pageNum) => (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="px-3 py-1 text-sm min-w-[32px]"
                        >
                          {pageNum}
                        </Button>
                      ),
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm"
                  >
                    التالي
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Student Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>تعديل بيانات الطالب</DialogTitle>
              <DialogDescription>
                تعديل بيانات الطالب: {editingStudent?.name}
              </DialogDescription>
            </DialogHeader>
            <Form {...editForm}>
              <form
                onSubmit={editForm.handleSubmit(onEditSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={editForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>اسم الطالب</FormLabel>
                      <FormControl>
                        <Input placeholder="أحمد محمد علي" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="grade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>الصف</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="اختر الصف" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableGrades.map((grade) => (
                            <SelectItem key={grade} value={grade}>
                              {grade}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="section"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>الشعبة</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="اختر الشعبة" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {editForm.watch("grade") &&
                            getAvailableSections(editForm.watch("grade")).map(
                              (section) => (
                                <SelectItem key={section} value={section}>
                                  شعبة {section}
                                </SelectItem>
                              )
                            )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* <FormField
                  control={editForm.control}
                  name="studentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>رقم الطالب</FormLabel>
                      <FormControl>
                        <Input placeholder="STU001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    حفظ التغييرات
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditDialogOpen(false)}
                    className="flex-1"
                  >
                    إلغاء
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* {isInviteModalOpen && ( */}
        <AlertDialog open={massDeleteOpen} onOpenChange={setMassDeleteOpen}>
          <AlertDialogContent dir="ltr" className="text-left">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-right">
                تأكيد الحذف النهائي للطلاب المحددين
              </AlertDialogTitle>
              <AlertDialogDescription className="text-right">
                سيتم حذف {selectedIds.size} طالباً نهائياً مع جميع البيانات المرتبطة
                بهم في النظام. لا يمكن التراجع عن هذا الإجراء.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingStudents}>
                إلغاء
              </AlertDialogCancel>
              <Button
                type="button"
                variant="destructive"
                disabled={deletingStudents}
                onClick={() => void handleMassDelete()}
              >
                {deletingStudents ? "جاري الحذف..." : "حذف نهائي"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={deleteAllSchoolOpen}
          onOpenChange={(open) => {
            setDeleteAllSchoolOpen(open);
            if (!open) {
              setDeleteAllTotalCount(null);
              setDeleteAllCountLoading(false);
            }
          }}
        >
          <AlertDialogContent
            dir="rtl"
            className="max-w-lg border-red-200 bg-red-50/95 text-right"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-lg text-red-950">
                <AlertTriangle className="h-6 w-6 shrink-0 text-red-600" />
                حذف جميع طلاب المدرسة — نهائي ولا رجوع
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-base text-slate-900">
                  <p className="font-semibold text-red-900">
                    أنت على وشك حذف كل سجلات الطلاب التابعة لهذه المدرسة من قاعدة
                    البيانات، وليس الطلاب الظاهرين في القائمة فقط.
                  </p>
                  <ul className="list-disc pr-5 space-y-1.5 text-slate-800">
                    <li>
                      <span className="font-medium">المدرسة:</span>{" "}
                      {settings?.school_name?.trim()
                        ? settings.school_name
                        : "هذه المدرسة"}
                    </li>
                    <li>
                      <span className="font-medium">نطاق الحذف:</span> كل الطلاب
                      بجميع الحالات (نشط، غير نشط، منقول، …) إن وُجدت.
                    </li>
                    <li>
                      <span className="font-medium">البيانات المرتبطة:</span>{" "}
                      سجلات الحضور والتأخر والإشعارات وتقدم الخطط والملفات
                      الشخصية المرتبطة بكل طالب — تُحذف مع الطالب.
                    </li>
                  </ul>
                  <p className="rounded-md border border-red-300 bg-white px-3 py-2 font-medium text-red-950">
                    {deleteAllCountLoading ? (
                      <>جاري حساب عدد الطلاب في المدرسة…</>
                    ) : deleteAllTotalCount !== null ? (
                      <>
                        سيتم حذف{" "}
                        <span className="text-xl font-bold tabular-nums text-red-700">
                          {deleteAllTotalCount}
                        </span>{" "}
                        طالباً نهائياً من قاعدة البيانات لهذه المدرسة (جميع
                        الحالات).
                      </>
                    ) : (
                      <>
                        تعذر جلب العدد الدقيق. إن واصلت، سيُحذف كل ما يظهر لدى
                        النظام من طلاب لهذه المدرسة.
                      </>
                    )}
                  </p>
                  <p className="text-sm font-semibold text-red-800">
                    لا يمكن استعادة هذه البيانات بعد التنفيذ.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-0">
              <AlertDialogCancel disabled={deletingStudents}>
                إلغاء — لا أريد الحذف
              </AlertDialogCancel>
              <Button
                type="button"
                variant="destructive"
                className="bg-red-800 hover:bg-red-900"
                disabled={
                  deletingStudents ||
                  deleteAllCountLoading ||
                  (deleteAllTotalCount !== null && deleteAllTotalCount === 0)
                }
                onClick={() => void handleDeleteAllSchoolStudents()}
              >
                {deletingStudents
                  ? "جاري الحذف…"
                  : "نعم — احذف جميع طلاب هذه المدرسة نهائياً"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Modal
          opened={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          title="دعوة الطالب عبر البريد الإلكتروني"
          size="md"
          centered
        >
          <Stack className="max-w-md m-auto">
            <TextInput
              value={studentToInvite?.email}
              onChange={(val) =>
                setStudentToInvite({
                  ...studentToInvite,
                  email: val.target.value,
                })
              }
              label="البريد الإلكتروني للطالب"
              required
            />

            <MantineBtn
              loading={studentToInvite?.loading === true}
              onClick={inviteUser}
              className="w-full"
            >
              دعوة الطالب
            </MantineBtn>
          </Stack>
        </Modal>
        {/* )} */}
      </div>
    </div>
  );
};

export default Students;
